import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Paper, TextField, Button, MenuItem, Select,
    InputLabel, FormControl, Alert, Table, TableBody, TableCell,
    TableRow, TableHead, Chip, CircularProgress, Grid, Checkbox, 
    ListItemText, Tabs, Tab, IconButton, Snackbar, Divider
} from "@mui/material";
import { styled } from "@mui/system";
import { db } from "../firebase";
import {
    collection, getDocs, addDoc, query, orderBy, deleteDoc, doc, updateDoc
} from "firebase/firestore";

// Icone
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// --- COSTANTI E HELPERS ---

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const DAY_NAME_TO_NUMBER = { 'Lunedì': 1, 'Martedì': 2, 'Mercoledì': 3, 'Giovedì': 4, 'Venerdì': 5, 'Sabato': 6, 'Domenica': 7 };

const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
};
const getMinutesDiff = (start, end) => {
  const [h1, m1] = start.split(':').map(Number);
  const [h2, m2] = end.split(':').map(Number);
  return (h2 * 60 + m2) - (h1 * 60 + m1);
};

const generateTimeSlots = (startHour = 7, endHour = 21, intervalMinutes = 15) => {
    const times = [];
    for (let h = startHour; h <= endHour; h++) {
        for (let m = 0; m < 60; m += intervalMinutes) {
            if (h === endHour && m > 0) continue;
            times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
    }
    return times;
};

const TABLE_TIMES = generateTimeSlots(7, 21, 15);
const FORM_HOUR_OPTIONS = generateTimeSlots(7, 21, 15);

// --- STYLED COMPONENTS ---

const MainContainer = styled(Box)({
    minHeight: "100vh",
    background: "#F8F9FA",
    padding: "30px",
    fontFamily: "'Inter', sans-serif",
});

const ModernCard = styled(Paper)({
    background: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
    padding: "24px",
    marginBottom: "24px",
    overflow: "hidden"
});

const SectionTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#2d3436",
    marginBottom: "20px",
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
});

// --- SOTTO-COMPONENTI ---

// 1. GESTORE CORSI (Piccolo e pulito)
const CourseManager = ({ courses, onAdd, onDelete, loading }) => {
    const [name, setName] = useState("");
    return (
        <ModernCard>
            <SectionTitle><SettingsIcon color="primary"/> Configurazione Corsi</SectionTitle>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField 
                    label="Nome Corso" size="small" value={name} onChange={(e) => setName(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <Button 
                    variant="contained" startIcon={<AddCircleIcon />} 
                    disabled={loading || !name.trim()}
                    onClick={() => { onAdd(name); setName(""); }}
                    sx={{ borderRadius: '12px', bgcolor: '#a32b2b', '&:hover': { bgcolor: '#802222' } }}
                >
                    Aggiungi
                </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {courses.map(c => (
                    <Chip 
                        key={c.id} label={c.name} onDelete={() => onDelete(c.id, c.name)} 
                        sx={{ fontWeight: 'bold', bgcolor: '#f1f2f6' }}
                    />
                ))}
            </Box>
        </ModernCard>
    );
};

// 2. FORM CREAZIONE SLOT (VERSIONE BLINDATA)
const SlotForm = ({ courses, loading, onSubmit }) => {
    const [courseName, setCourseName] = useState("");
    const [selectedDays, setSelectedDays] = useState([]);
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [maxCapacity, setMaxCapacity] = useState(10);
    const [instructor, setInstructor] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({ courseName, selectedDays, startTime, endTime, maxCapacity, instructor });
        setSelectedDays([]); setStartTime(""); setEndTime("");
    };

    // --- STILE FORZATO ---
    // Questo impedisce ai campi di diventare minuscoli
    const forcedStyle = {
        width: '100%',
        minWidth: '120px', // Nessun campo sarà mai più piccolo di 120px
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.1)',
        }
    };

    return (
        <ModernCard>
            <SectionTitle><AddCircleIcon color="primary"/> Crea Nuovi Orari</SectionTitle>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    
                    {/* RIGA 1: Corso (Largo) e Giorni (Largo) */}
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" sx={{ minWidth: '200px' }}>
                            <InputLabel>Corso</InputLabel>
                            <Select 
                                value={courseName} 
                                label="Corso" 
                                onChange={(e) => setCourseName(e.target.value)}
                                sx={forcedStyle}
                            >
                                {courses.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={8}>
                        <FormControl fullWidth size="small" sx={{ minWidth: '200px' }}>
                            <InputLabel>Giorni</InputLabel>
                            <Select
                                multiple 
                                value={selectedDays} 
                                label="Giorni"
                                onChange={(e) => setSelectedDays(e.target.value)}
                                renderValue={(selected) => selected.join(", ")}
                                sx={forcedStyle}
                            >
                                {DAYS.map(d => (
                                    <MenuItem key={d} value={d}>
                                        <Checkbox checked={selectedDays.indexOf(d) > -1} />
                                        <ListItemText primary={d} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* RIGA 2: Orari e Dettagli (Minimo 120px ognuno) */}
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small" sx={{ minWidth: '100px' }}>
                            <InputLabel>Inizio</InputLabel>
                            <Select 
                                value={startTime} label="Inizio" onChange={(e) => setStartTime(e.target.value)}
                                sx={forcedStyle}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                            >
                                {FORM_HOUR_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small" sx={{ minWidth: '100px' }}>
                            <InputLabel>Fine</InputLabel>
                            <Select 
                                value={endTime} label="Fine" onChange={(e) => setEndTime(e.target.value)}
                                sx={forcedStyle}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                            >
                                {FORM_HOUR_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField 
                            label="Max" type="number" size="small" fullWidth 
                            value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': forcedStyle }}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3} md={3}>
                        <TextField 
                            label="Istruttore" size="small" fullWidth 
                            value={instructor} onChange={(e) => setInstructor(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': forcedStyle }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Button 
                            type="submit" variant="contained" fullWidth size="large" 
                            disabled={loading}
                            sx={{ borderRadius: '12px', bgcolor: '#a32b2b', height: '40px', fontWeight: 'bold' }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit"/> : "Crea Slot"}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </ModernCard>
    );
};

// 3. CALENDARIO SETTIMANALE (Il "Cuore" visivo)
const WeeklyCalendar = ({ slots, loading }) => {
    const getSlot = (day, time) => slots.find(s => s.day === day && s.startTime === time && s.isActive !== false);

    if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <ModernCard sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #eee' }}>
                <SectionTitle sx={{ mb: 0 }}><CalendarMonthIcon color="primary"/> Planning Settimanale</SectionTitle>
            </Box>
            <Box sx={{ overflowX: "auto", maxHeight: "80vh" }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ bgcolor: '#fff', zIndex: 10, width: 60 }}>Ora</TableCell>
                            {DAYS.map(d => <TableCell key={d} align="center" sx={{ bgcolor: '#f8f9fa', fontWeight: 'bold' }}>{d}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                       {TABLE_TIMES.map(time => (
    <TableRow key={time}>
        <TableCell sx={{ bgcolor: '#fff', fontWeight: 'bold', color: '#b2bec3', borderRight: '1px solid #f1f2f6' }}>{time}</TableCell>
        {DAYS.map(day => {
            const slot = getSlot(day, time);
            
            // Check standard per vedere se la cella è già mangiata da un rowSpan precedente
            const isCoveredByPrevious = slots.some(s => s.day === day && s.isActive !== false && s.startTime < time && s.endTime > time);
            if (isCoveredByPrevious) return null; 

            if (!slot) return <TableCell key={day} sx={{ borderBottom: '1px solid #f8f9fa', borderRight: '1px solid #f8f9fa' }} />;

            const rowSpan = (slot.durationMinutes || 60) / 15;
            
            // --- QUI STA LA MAGIA ---
            // Cerchiamo corsi che iniziano DURANTE questo slot principale (quelli che verrebbero mangiati)
            const overlappingSlots = slots.filter(s => 
                s.day === day && 
                s.startTime > slot.startTime && // Inizia dopo il principale
                s.startTime < slot.endTime // Ma inizia prima che il principale finisca
            );

            // Styling dinamico (funzione helper per pulire il codice)
            const getSlotStyle = (s) => {
                const isCross = s.courseName.toLowerCase().includes("crossfit");
                const isPilates = s.courseName.toLowerCase().includes("pilates");
                return {
                    bg: isCross ? '#e3f2fd' : (isPilates ? '#fce4ec' : '#e8f5e9'),
                    color: isCross ? '#1565c0' : (isPilates ? '#c2185b' : '#2e7d32')
                };
            };

            const mainStyle = getSlotStyle(slot);

            return (
                <TableCell 
                    key={day} rowSpan={rowSpan} 
                    sx={{ 
                        bgcolor: mainStyle.bg, 
                        borderLeft: `4px solid ${mainStyle.color}`, 
                        p: 1, 
                        verticalAlign: 'top', // Meglio top se ci sono sovrapposizioni
                        cursor: 'pointer',
                        position: 'relative', // FONDAMENTALE per i figli assoluti
                        boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.05)'
                    }}
                >
                    {/* --- SLOT PRINCIPALE --- */}
                    <Box sx={{ width: overlappingSlots.length > 0 ? '50%' : '100%' }}>
                        <Typography variant="subtitle2" sx={{ color: mainStyle.color, fontWeight: 'bold', lineHeight: 1.2 }}>
                            {slot.courseName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                            {slot.startTime} - {slot.endTime}
                        </Typography>
                        {slot.instructor && (
                            <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
                                {slot.instructor}
                            </Typography>
                        )}
                    </Box>

                    {/* --- SLOT SOVRAPPOSTI (PARASSITI) --- */}
                    {overlappingSlots.map((subSlot, idx) => {
                        const subStyle = getSlotStyle(subSlot);
                        
                        // Calcolo brutale della posizione
                        // Esempio: Main dura 120min. Sub inizia 60min dopo. Top = 50%.
                        const mainDuration = slot.durationMinutes || 60;
                        // Nota: qui serve una funzione che calcola la differenza in minuti tra due orari "HH:mm"
                        // Assumo tu abbia una helper 'getMinutesDiff(start, end)' o simile.
                        // Se non ce l'hai, scrivila al volo, è matematica elementare.
                        const minutesFromStart = getMinutesDiff(slot.startTime, subSlot.startTime); 
                        const topPercentage = (minutesFromStart / mainDuration) * 100;
                        const subDuration = subSlot.durationMinutes || 60;
                        const heightPercentage = (subDuration / mainDuration) * 100;

                        return (
                            <Box 
                                key={idx}
                                sx={{
                                    position: 'absolute',
                                    top: `${topPercentage}%`,
                                    right: 0,
                                    width: '50%', // Si prende la metà destra
                                    height: `${heightPercentage}%`,
                                    bgcolor: subStyle.bg,
                                    borderLeft: `4px solid ${subStyle.color}`,
                                    p: 1,
                                    zIndex: 2,
                                    overflow: 'hidden',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: subStyle.color, fontWeight: 'bold', fontSize: '0.75rem' }}>
                                    {subSlot.courseName}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                                    {subSlot.startTime}
                                </Typography>
                            </Box>
                        )
                    })}
                </TableCell>
            );
        })}
    </TableRow>
))}
                    </TableBody>
                </Table>
            </Box>
        </ModernCard>
    );
};

// 4. LISTA TESTUALE (Per cancellazioni)
const SlotList = ({ slots, onDelete, onToggle }) => (
    <ModernCard>
        <SectionTitle><ListAltIcon color="primary"/> Elenco Completo</SectionTitle>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Corso</TableCell>
                    <TableCell>Giorno</TableCell>
                    <TableCell>Orario</TableCell>
                    <TableCell align="center">Stato</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {slots.map(s => (
                    <TableRow key={s.id} sx={{ opacity: s.isActive === false ? 0.5 : 1 }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{s.courseName}</TableCell>
                        <TableCell>{s.day}</TableCell>
                        <TableCell>{s.startTime} - {s.endTime}</TableCell>
                        <TableCell align="center">
                            <Chip 
                                label={s.isActive === false ? "Nascosto" : "Attivo"} 
                                color={s.isActive === false ? "default" : "success"} size="small" variant="outlined" 
                            />
                        </TableCell>
                        <TableCell align="right">
                             <IconButton size="small" color={s.isActive ? "warning" : "success"} onClick={() => onToggle(s.id, s.isActive)}>
                                <AccessTimeIcon />
                             </IconButton>
                             <IconButton size="small" color="error" onClick={() => onDelete(s.id)}>
                                <DeleteIcon />
                             </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </ModernCard>
);


// --- MAIN SCREEN ---

export default function GymActivitiesScreen() {
    const [slots, setSlots] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [feedback, setFeedback] = useState({ open: false, msg: "" });

    const showMsg = (msg) => setFeedback({ open: true, msg });

    // FETCH DATA
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Corsi
            const cSnap = await getDocs(query(collection(db, "courseList"), orderBy("name")));
            setCourses(cSnap.docs.map(d => ({ id: d.id, name: d.data().name })));
            
            // Slots
            const sSnap = await getDocs(query(collection(db, "activitySlots"), orderBy("dayIndex"), orderBy("startTime")));
            setSlots(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { showMsg("Errore caricamento dati"); }
        setLoading(false);
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ACTIONS
    const addCourse = async (name) => {
        if (courses.some(c => c.name.toLowerCase() === name.toLowerCase().trim())) return showMsg("Corso esistente!");
        await addDoc(collection(db, "courseList"), { name: name.trim() });
        showMsg("Corso aggiunto"); fetchData();
    };

    const deleteCourse = async (id, name) => {
        if (!window.confirm(`Eliminare ${name}?`)) return;
        await deleteDoc(doc(db, "courseList", id));
        showMsg("Corso eliminato"); fetchData();
    };

const createSlot = async (data) => {
        const { courseName, selectedDays, startTime, endTime, maxCapacity, instructor } = data;
        if (!courseName || selectedDays.length === 0 || !startTime || !endTime) return showMsg("Dati mancanti!");
        
        const newStart = parseTime(startTime);
        const newEnd = parseTime(endTime);
        const duration = newEnd - newStart;
        
        if (duration <= 0) return showMsg("Orario di fine deve essere successivo a quello di inizio!");

        // --- NUOVO CONTROLLO DI SOVRAPPOSIZIONE ---
        for (const day of selectedDays) {
            
            // 1. Filtra gli slot esistenti per il giorno corrente e lo stesso corso
            const existingSlotsForDay = slots.filter(
                s => s.day === day && 
                     s.courseName === courseName && 
                     s.isActive !== false // Controlla solo slot attivi
            );

            // 2. Esegui il controllo di sovrapposizione per ogni slot esistente
            for (const existingSlot of existingSlotsForDay) {
                const existingStart = parseTime(existingSlot.startTime);
                const existingEnd = parseTime(existingSlot.endTime);
                
                // Criterio di sovrapposizione:
                // Se (NewStart < ExistingEnd) E (NewEnd > ExistingStart), c'è sovrapposizione.
                // (Es. Nuovo: 15:15-16:15; Esistente: 15:00-16:00)
                // NewStart (915) < ExistingEnd (960) -> TRUE
                // NewEnd (975) > ExistingStart (900) -> TRUE
                // Risultato: Sovrapposizione!
                
                if (newStart < existingEnd && newEnd > existingStart) {
                    showMsg(`ERRORE: Sovrapposizione per ${courseName} di ${day}! Lo slot esistente è ${existingSlot.startTime} - ${existingSlot.endTime}.`);
                    return; // Interrompe l'intera funzione
                }
            }
        }
        // --- FINE CONTROLLO SOVRAPPOSIZIONE ---

        setLoading(true);
        for (const day of selectedDays) {
            // Aggiungi solo se non ci sono sovrapposizioni
            await addDoc(collection(db, "activitySlots"), {
                courseName, type: "Corso", day, dayIndex: DAY_NAME_TO_NUMBER[day],
                startTime, endTime, durationMinutes: duration, maxCapacity: parseInt(maxCapacity),
                instructor, isActive: true, createdAt: new Date()
            });
        }
        showMsg("Slot Creati!"); fetchData();
    };

    const deleteSlot = async (id) => {
        if (!window.confirm("Eliminare slot?")) return;
        await deleteDoc(doc(db, "activitySlots", id));
        showMsg("Slot eliminato"); fetchData();
    };

    const toggleSlot = async (id, currentStatus) => {
        await updateDoc(doc(db, "activitySlots", id), { isActive: !currentStatus });
        showMsg("Stato aggiornato"); fetchData();
    };

    return (
        <MainContainer>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>Programmazione Orari</Typography>
                <Typography variant="body2" color="text.secondary">Gestisci corsi, istruttori e calendario settimanale.</Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
                    <Tab label="Calendario Visivo" icon={<CalendarMonthIcon/>} iconPosition="start"/>
                    <Tab label="Gestione & Config" icon={<SettingsIcon/>} iconPosition="start"/>
                    <Tab label="Lista Dettagliata" icon={<ListAltIcon/>} iconPosition="start"/>
                </Tabs>
            </Box>

            {tab === 0 && <WeeklyCalendar slots={slots} loading={loading} />}
            
            {tab === 1 && (
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={4}>
                        <CourseManager courses={courses} onAdd={addCourse} onDelete={deleteCourse} loading={loading} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <SlotForm courses={courses} loading={loading} onSubmit={createSlot} />
                    </Grid>
                </Grid>
            )}

            {tab === 2 && <SlotList slots={slots} onDelete={deleteSlot} onToggle={toggleSlot} />}

            <Snackbar 
                open={feedback.open} autoHideDuration={3000} 
                onClose={() => setFeedback({ ...feedback, open: false })}
                message={feedback.msg}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </MainContainer>
    );
}